import { useOutletContext } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  Button,
  Badge,
  Tooltip,
  toast,
  Stack,
} from "@altimateai/lego";
import { IconRefresh, IconAlertTriangle, IconReload } from "@tabler/icons-react";
import { formatDate } from "@/helpers/utils";
import {
  getStatusColor,
  getStatusIcon,
  getStatusLabel,
  isTerminalStatus,
  isFailedStatus,
  getProcessingTimeInfo,
  getPhaseProgress,
  toUserFriendlyError,
} from "../utils/statusHelpers";
import { getKnowledgeBaseDocumentHistory, retryKnowledgeDocumentProcessing } from "@/helpers/apis";
import { KnowledgeTracking } from "@/lib/tracking/knowledge";

// Polling interval for non-terminal statuses (30 seconds)
const POLLING_INTERVAL_MS = 30 * 1000;

interface OutletContext {
  kbId?: number;
  getUserInfo: (userId?: number) => string | null;
}

export default function HistoryTab() {
  const { kbId, getUserInfo } = useOutletContext<OutletContext>();
  const queryClient = useQueryClient();

  const {
    data: documentHistory,
    refetch: refetchDocumentHistory,
    isFetching: isRefreshingHistory,
  } = useQuery({
    queryKey: ["knowledge-base-documents", kbId],
    queryFn: () => getKnowledgeBaseDocumentHistory(Number(kbId)),
    enabled: !!kbId,
    // Poll every 30 seconds if any document is still processing
    refetchInterval: (query) => {
      const docs = query.state.data;
      if (!docs || docs.length === 0) return false;
      const hasProcessingDocs = docs.some(
        (doc: { processing_status: string }) => !isTerminalStatus(doc.processing_status)
      );
      return hasProcessingDocs ? POLLING_INTERVAL_MS : false;
    },
  });

  const retryMutation = useMutation({
    mutationFn: (docId: number) => retryKnowledgeDocumentProcessing(Number(kbId), docId),
    onSuccess: (_, docId) => {
      KnowledgeTracking.processingRetried(Number(kbId), docId);
      toast({
        title: "Retry queued",
        description: "Document processing has been queued for retry.",
      });
      queryClient.invalidateQueries({ queryKey: ["knowledge-base-documents", kbId] });
      queryClient.invalidateQueries({ queryKey: ["knowledge-base-v2", kbId] });
    },
    onError: (error: Error) => {
      toast({
        title: "Retry failed",
        description: error.message || "Failed to retry document processing.",
        variant: "destructive",
      });
    },
  });
  return (
    <Card>
      <CardHeader>
        <Stack direction="row" justify="between" align="center">
          <Typography variant="h4" weight="medium">
            Document Upload History
          </Typography>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetchDocumentHistory()}
            disabled={isRefreshingHistory}
          >
            <IconRefresh
              className={`al-w-4 al-h-4 al-mr-2 ${isRefreshingHistory ? "al-animate-spin" : ""}`}
            />
            Refresh
          </Button>
        </Stack>
      </CardHeader>
      <CardContent>
        {documentHistory && documentHistory.length > 0 ? (
          <div className="al-overflow-x-auto">
            <table className="al-w-full al-border-collapse">
              <thead>
                <tr className="al-border-b">
                  <th className="al-text-left al-p-3 al-text-sm al-font-medium al-text-gray-600">
                    Version
                  </th>
                  <th className="al-text-left al-p-3 al-text-sm al-font-medium al-text-gray-600">
                    Filename
                  </th>
                  <th className="al-text-left al-p-3 al-text-sm al-font-medium al-text-gray-600">
                    Type
                  </th>
                  <th className="al-text-left al-p-3 al-text-sm al-font-medium al-text-gray-600">
                    Size
                  </th>
                  <th className="al-text-left al-p-3 al-text-sm al-font-medium al-text-gray-600">
                    Status
                  </th>
                  <th className="al-text-left al-p-3 al-text-sm al-font-medium al-text-gray-600">
                    Uploaded By
                  </th>
                  <th className="al-text-left al-p-3 al-text-sm al-font-medium al-text-gray-600">
                    Uploaded On
                  </th>
                </tr>
              </thead>
              <tbody>
                {documentHistory.map(doc => (
                  <tr key={doc.id} className="al-border-b hover:al-bg-gray-50">
                    <td className="al-p-3 al-text-sm">
                      <Stack direction="row" align="center" gap="xs">
                        <Typography variant="body" size="sm">
                          v{doc.version}
                        </Typography>
                        {doc.is_active && (
                          <Badge variant="default" className="al-text-xs">
                            Active
                          </Badge>
                        )}
                      </Stack>
                    </td>
                    <td className="al-p-3 al-text-sm">
                      <Typography variant="body" size="sm">
                        {doc.original_filename}
                      </Typography>
                    </td>
                    <td className="al-p-3 al-text-sm">
                      <Typography variant="body" size="sm">
                        {doc.file_type}
                      </Typography>
                    </td>
                    <td className="al-p-3 al-text-sm">
                      <Typography variant="body" size="sm">
                        {(doc.file_size_bytes / 1024).toFixed(2)} KB
                      </Typography>
                    </td>
                    <td className="al-p-3 al-text-sm">
                      <Stack direction="column" gap="xs">
                        <Stack direction="row" align="center" gap="sm">
                          <Badge variant={getStatusColor(doc.processing_status)}>
                            <Stack direction="row" align="center" gap="xs">
                              {getStatusIcon(doc.processing_status)}
                              {getStatusLabel(doc.processing_status)}
                            </Stack>
                          </Badge>
                          {isFailedStatus(doc.processing_status) && (
                            <>
                              <Tooltip title={toUserFriendlyError(doc.processing_error)}>
                                  <span>
                                    <IconAlertTriangle className="al-w-4 al-h-4 al-text-destructive al-cursor-help" />
                                  </span>
                                </Tooltip>
                              <Tooltip title="Retry processing">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => retryMutation.mutate(doc.id)}
                                  disabled={retryMutation.isPending}
                                  className="al-h-6 al-px-2"
                                >
                                  <IconReload
                                    className={`al-w-3 al-h-3 ${retryMutation.isPending ? "al-animate-spin" : ""}`}
                                  />
                                </Button>
                              </Tooltip>
                            </>
                          )}
                        </Stack>
                        {(() => {
                          const phaseProgress = getPhaseProgress(doc.processing_status);
                          const timeInfo = getProcessingTimeInfo(
                            doc.file_size_bytes,
                            doc.processing_status,
                            doc.processing_started_at
                          );
                          if (!timeInfo.showTimeInfo && !phaseProgress) return null;
                          return (
                            <Stack direction="column" gap="xs">
                              {phaseProgress && (
                                <Typography variant="body" size="sm" className="al-text-muted-foreground">
                                  Step {phaseProgress.currentPhase} of {phaseProgress.totalPhases}: {phaseProgress.phaseName} ({phaseProgress.percentage}%)
                                </Typography>
                              )}
                              {timeInfo.showTimeInfo && (
                                <>
                                  <Typography variant="body" size="sm" className="al-text-muted-foreground">
                                    {timeInfo.elapsedText}
                                  </Typography>
                                  <Typography variant="body" size="sm" className="al-text-muted-foreground">
                                    {timeInfo.estimateText}
                                  </Typography>
                                  {timeInfo.isExceeding && (
                                    <Typography variant="body" size="sm" className="al-text-amber-600">
                                      Taking longer than expected
                                    </Typography>
                                  )}
                                </>
                              )}
                            </Stack>
                          );
                        })()}
                      </Stack>
                    </td>
                    <td className="al-p-3 al-text-sm">
                      <Typography variant="body" size="sm">
                        {getUserInfo(doc.user_id) || "-"}
                      </Typography>
                    </td>
                    <td className="al-p-3 al-text-sm">
                      <Typography variant="body" size="sm">
                        {formatDate(doc.created_at)}
                      </Typography>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <Typography variant="body">No document history available.</Typography>
        )}
      </CardContent>
    </Card>
  );
}
